import { z } from "zod";

import { colors } from "#app/theme/v1/colors";
import { SimilarPartFeedbackButtons } from "#modules/feedback/view/SimilarPartFeedbackButtons";
import { usePartFeatures } from "#modules/part/view/usePartFeatures";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { Features } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/Features/Features";
import { SearchSimilarPartDto } from "#modules/similarPart/infra/searchSimilarParts.api";
import { PartName } from "#modules/similarPart/view/PartName";
import { Group } from "#shared/view/components/Group";
import { SectionWrapper } from "#shared/view/components/SectionWrapper";
import SimilarityScore from "#shared/view/components/SimilarityScore";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { ThumbnailOverlay } from "#shared/view/components/Thumbnails/ThumbnailOverlay";
import { File, ProcessStepSchema, QuoteRequestLineSchema } from "shared";
import { MaterialGradeType } from "shared";

import { PartChips } from "./part/PartChips";

type Props = {
  part: Omit<SearchSimilarPartDto, "quoteRequestLines"> & {
    cosineSimilarity?: number;
    quoteRequestLines?: (z.infer<typeof QuoteRequestLineSchema> & {
      processSteps?: z.infer<typeof ProcessStepSchema>[];
    })[];
    files: File[];
  };
  onClick?: () => void;
  quantity?: number;
  quoteRequestLine?: QuoteRequestLineDto | undefined | null;
  quoteRequestLineMaterialGrade?: MaterialGradeType | null;
  featuresDisplayMode?: "all" | "truncate" | "none";
  onSubmitPartDimensions?: () => void;
  dimensionsUpdatable?: boolean;
  enable3DViewer?: boolean;
  displayScore?: boolean;
  sourcePartId?: string;
  index: number;
};

export const PartCard = ({
  part,
  onClick,
  quantity,
  quoteRequestLineMaterialGrade,
  featuresDisplayMode,
  quoteRequestLine,
  onSubmitPartDimensions,
  dimensionsUpdatable,
  enable3DViewer = true,
  displayScore = false,
  sourcePartId,
  index,
}: Props) => {
  const { features } = usePartFeatures(part.id);

  const isSamePart = (part.cosineSimilarity || 0) === 1;

  return (
    <SectionWrapper key={part.id} onClick={onClick} style={isSamePart ? sameCard : undefined}>
      <Stack gap="xxs">
        <Group justify="space-between" gap={"xs"} wrap="nowrap">
          <Group
            style={{
              minWidth: "50%",
              maxWidth: "100%",
            }}
            gap={0}
            wrap="nowrap"
          >
            <PartName partName={part.name} style={{ flex: 1 }} />
          </Group>
          {displayScore && <SimilarityScore similarity={part.cosineSimilarity} />}
        </Group>
        <Stack gap="xs">
          <ThumbnailOverlay
            part={part}
            size={{ width: "100%", height: "100%" }}
            onClick={enable3DViewer ? undefined : onClick}
          />

          <Stack gap="xxs">
            <PartChips
              part={part}
              quoteRequestLineMaterialGrade={quoteRequestLineMaterialGrade}
              quoteRequestLine={quoteRequestLine}
              onSubmitPartDimensions={onSubmitPartDimensions}
              dimensionsUpdatable={dimensionsUpdatable}
            />
            {featuresDisplayMode !== "none" && !!features.length && (
              <Features partId={part.id} displayMode={featuresDisplayMode} partType={part.type} />
            )}
          </Stack>
          {!!quantity && <Text>Quantité : {quantity}</Text>}

          <SimilarPartFeedbackButtons
            sourcePartId={sourcePartId}
            targetPartId={part.id}
            index={index}
          />
        </Stack>
      </Stack>
    </SectionWrapper>
  );
};

const sameCard = {
  backgroundColor: colors.green[1],
  borderColor: colors.green[4],
};
